import { formatMoney, formatMoneyPreservePrecision, currencyDecimals } from '@/lib/money';

export interface DualCurrencyMoneyProps {
  /** USD amount (primary). Always shown first and most prominently. */
  usdAmount: string | number | null | undefined;
  /** Selected-currency amount (secondary, in parentheses). Already converted upstream. */
  selectedAmount?: string | number | null;
  /** Selected currency code (e.g. "EUR"). When "USD" or empty, only USD is shown. */
  selectedCurrency?: string | null;
  /** Explicit USD decimal places (defaults to currency decimals / 2). */
  usdDecimalPlaces?: number;
  /** Explicit selected-currency decimal places (defaults to currency decimals). */
  selectedDecimalPlaces?: number;
  /** Preserve the USD input string's own precision (canonical unit prices like "0.670000"). */
  preserveUsdPrecision?: boolean;
  /** 'inline' renders both on one line; 'stack' puts secondary on a second line. */
  secondaryPosition?: 'inline' | 'stack';
  /** Smaller primary size (text-sm instead of the default). */
  compact?: boolean;
  /** Optional trailing label appended to both amounts (e.g. "/ pc"). */
  suffix?: string;
  className?: string;
  primaryClassName?: string;
  secondaryClassName?: string;
}

export default function DualCurrencyMoney({
  usdAmount,
  selectedAmount,
  selectedCurrency,
  usdDecimalPlaces,
  selectedDecimalPlaces,
  preserveUsdPrecision,
  secondaryPosition = 'inline',
  compact,
  suffix,
  className = '',
  primaryClassName,
  secondaryClassName,
}: DualCurrencyMoneyProps) {
  const code = (selectedCurrency || '').toUpperCase();
  const hasUsd = usdAmount != null && usdAmount !== '';
  const hasSelected = selectedAmount != null && selectedAmount !== '';
  const showSecondary = hasUsd && hasSelected && !!code && code !== 'USD';

  const defaultPrimary = compact
    ? 'text-sm font-semibold text-ink-700'
    : 'font-semibold text-ink-700';

  // Selected-only fallback (e.g. Final Offer rows before Backend adds USD fields).
  if (!hasUsd && hasSelected && code && code !== 'USD') {
    const dp = selectedDecimalPlaces ?? currencyDecimals(code);
    return (
      <span className={primaryClassName ?? defaultPrimary}>
        {formatMoney(selectedAmount as string | number, code, dp)}
        {suffix ? ` ${suffix}` : ''}
      </span>
    );
  }

  if (!hasUsd) return null;

  const usdText = preserveUsdPrecision
    ? formatMoneyPreservePrecision(usdAmount as string | number, 'USD', usdDecimalPlaces ?? 0)
    : formatMoney(usdAmount as string | number, 'USD', usdDecimalPlaces);

  const primary = suffix ? `${usdText} ${suffix}` : usdText;

  if (!showSecondary) {
    return <span className={primaryClassName ?? defaultPrimary}>{primary}</span>;
  }

  const dp = selectedDecimalPlaces ?? currencyDecimals(code);
  const secondary = suffix
    ? `(${formatMoney(selectedAmount as string | number, code, dp)} ${suffix})`
    : `(${formatMoney(selectedAmount as string | number, code, dp)})`;

  if (secondaryPosition === 'stack') {
    return (
      <span className={`inline-flex flex-col items-start ${className}`}>
        <span className={primaryClassName ?? defaultPrimary}>{primary}</span>
        <span className={secondaryClassName ?? 'text-xs font-normal text-ink-400'}>{secondary}</span>
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 ${className}`}>
      <span className={primaryClassName ?? defaultPrimary}>{primary}</span>
      <span className={secondaryClassName ?? 'text-xs font-normal text-ink-400'}>{secondary}</span>
    </span>
  );
}